import React from 'react'
import { useParams } from 'react-router-dom'
import {
  usePRFileSections,
  PRMarinaFileSection,
} from '@sketch-hq/sketch-web-renderer'
import {
  useResponsiveDropdown,
  useBreakpoint,
  SFSymbolLabel,
} from '@sketch/components'
import { RouteParams, routes } from '@sketch/modules-common'

import { getNearestSectionAncestorUUID } from '../../../../canvas/utils'
import {
  Trigger,
  CurrentSection,
  Chevrons,
  Container,
  StyledDropdownNavItem,
} from './SectionSelector.styles'

const DROPDOWN_STYLES = {
  minWidth: 160,
  overflowY: 'auto',
  maxHeight: '80vh',
} as const

/**
 * The current Section's siblings - the Sections sharing its nearest Section
 * ancestor (all top-level ones when it has none). Read from the renderer rather
 * than GQL: a Preview has no share to query, and the node tree already knows how
 * the Sections nest.
 */
function useSiblingSections(
  sections: PRMarinaFileSection[],
  currentSectionUUID: string
) {
  const currentParentUUID = getNearestSectionAncestorUUID(
    sections
      .find(section => section.objectId === currentSectionUUID)
      ?.getNode() ?? null
  )

  return sections.filter(
    section =>
      getNearestSectionAncestorUUID(section.getNode()) === currentParentUUID
  )
}

interface SectionListForPreviewProps {
  previewID: string
  pageUUID: string
  currentSectionUUID: string
  sections: PRMarinaFileSection[]
}

function SectionListForPreview({
  previewID,
  pageUUID,
  currentSectionUUID,
  sections,
}: SectionListForPreviewProps) {
  return (
    <>
      {sections.map(section => {
        const pathname = routes.PREVIEW_PAGE_SECTION.create({
          previewID,
          pageUUID,
          sectionUUID: section.objectId,
        })

        return (
          <StyledDropdownNavItem
            key={section.objectId}
            data-testid="section-selector-item"
            to={pathname}
            isActive={match =>
              Boolean(match) || section.objectId === currentSectionUUID
            }
            exact
          >
            <SFSymbolLabel text={section.name} />
          </StyledDropdownNavItem>
        )
      })}
    </>
  )
}

/**
 * The Section equivalent of FrameSelectorForPreview: switches between the
 * focused Section's siblings from the Preview canvas panel.
 */
export function SectionSelectorForPreview() {
  const { previewID, pageUUID, sectionUUID } =
    useParams<Partial<RouteParams<'PREVIEW_PAGE_SECTION'>>>()

  const allSections = usePRFileSections() ?? []
  const sections = useSiblingSections(allSections, sectionUUID ?? '')
  const currentSection = allSections.find(
    section => section.objectId === sectionUUID
  )
  const isMobile = !useBreakpoint('sm')

  const [content, buttonProps] = useResponsiveDropdown({
    dropdown: SectionListForPreview,
    dropdownProps: {
      previewID: previewID ?? '',
      pageUUID: pageUUID ?? '',
      currentSectionUUID: sectionUUID ?? '',
      sections,
    },
    usePortal: true,
    placement: isMobile ? 'top-start' : 'bottom-start',
    dropdownStyle: DROPDOWN_STYLES,
    offset: [-8, 13], // 13 = 5 (usual offset) + 8 (canvas panel bottom padding)
  })

  const isDisabled = sections.length <= 1

  return (
    <Container>
      <Trigger
        {...buttonProps}
        aria-label="Canvas sections list"
        data-testid="sections-selector-trigger"
        disabled={isDisabled}
      >
        <CurrentSection>
          {currentSection ? (
            <SFSymbolLabel text={currentSection.name} />
          ) : (
            'Select section...'
          )}
        </CurrentSection>
        {!isDisabled && <Chevrons />}
      </Trigger>
      {!isDisabled && content}
    </Container>
  )
}
